/**
 * Environment-Aware Logging System
 * Provides structured logging with environment-specific behavior
 */

import * as Sentry from "@sentry/vue";

import { EnvironmentConfig } from "../config/environment.js";

export class Logger {
  // Current log level based on environment
  static get currentLevel() {
    // Use manually set level if available, otherwise use environment config
    const level = this._logLevel || EnvironmentConfig.logLevel;
    return this.LOG_LEVELS[level] || this.LOG_LEVELS.info;
  }

  // Structured logging method
  static log(level, message, data = null, context = null) {
    const levelPriority = this.LOG_LEVELS[level] || this.LOG_LEVELS.info;

    // Skip if log level is below current threshold
    if (levelPriority < this.currentLevel) {
      return;
    }

    // Create log entry
    const logEntry = this.createLogEntry(level, message, data, context);

    // Output to console
    this.outputToConsole(logEntry);

    // Send to external services in production, and on staging — staging is a
    // full pre-prod mirror, and without it there is no deployed environment in
    // which error reporting can be verified before it reaches production.
    if (EnvironmentConfig.isProduction || EnvironmentConfig.isStaging) {
      this.sendToExternalServices(logEntry);
    }

    // Store in recent logs for debugging
    this.storeRecentLog(logEntry);
  }

  // Create structured log entry
  static createLogEntry(level, message, data, context) {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      context,
      environment: EnvironmentConfig.environment,
      buildInfo: {
        version: process.env.VUE_APP_VERSION || "unknown",
        commit: process.env.VUE_APP_GIT_COMMIT_SHORT || "unknown",
      },
      session: this.getSessionInfo(),
    };
  }

  // Output to browser console with appropriate formatting
  static outputToConsole(logEntry) {
    const { level, message, data, context, timestamp } = logEntry;
    const color = this.COLORS[level] || this.COLORS.info;

    // Format timestamp for display
    const time = new Date(timestamp).toLocaleTimeString();

    // Create console message parts
    const prefix = `%c[${time}] ${level.toUpperCase()}`;
    const contextPart = context ? ` (${context})` : "";
    const fullMessage = `${prefix}${contextPart}: ${message}`;

    // Use appropriate console method
    const consoleMethod = console[level] || console.log;

    if (data) {
      consoleMethod(fullMessage, `color: ${color}; font-weight: bold`, data);
    } else {
      consoleMethod(fullMessage, `color: ${color}; font-weight: bold`);
    }
  }

  // Send logs to external services
  static sendToExternalServices(logEntry) {
    // Send to Sentry if configured
    if (EnvironmentConfig.monitoring?.sentryDsn) {
      this.sendToSentry(logEntry);
    }

    // Send to custom logging endpoint
    const customEndpoint =
      this._customLoggingEndpoint || process.env.VUE_APP_LOGGING_ENDPOINT;
    if (customEndpoint) {
      this.sendToCustomEndpoint(logEntry, customEndpoint);
    }
  }

  // Send to Sentry
  //
  // Uses the imported SDK rather than a `window.Sentry` global: main.js does
  // `import * as Sentry from "@sentry/vue"` and never publishes it globally, so
  // gating on the global silently dropped every log. If Sentry.init() hasn't
  // run (dev/test builds with no DSN) these calls are no-ops in the SDK.
  static sendToSentry(logEntry) {
    try {
      if (logEntry.level === "error") {
        Sentry.captureException(new Error(logEntry.message), {
          extra: logEntry.data,
          tags: {
            context: logEntry.context,
            environment: logEntry.environment,
          },
        });
      } else {
        Sentry.captureMessage(logEntry.message, logEntry.level);
      }
    } catch (error) {
      console.warn("Failed to send log to Sentry:", error);
    }
  }

  // Send to custom logging endpoint
  static sendToCustomEndpoint(logEntry, endpoint = null) {
    try {
      const customEndpoint =
        endpoint ||
        this._customLoggingEndpoint ||
        process.env.VUE_APP_LOGGING_ENDPOINT;
      if (!customEndpoint) return;

      // Don't block main thread
      setTimeout(() => {
        fetch(customEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(logEntry),
        }).catch((error) => {
          console.warn("Failed to send log to custom endpoint:", error);
        });
      }, 0);
    } catch (error) {
      console.warn("Failed to send log to custom endpoint:", error);
    }
  }

  // Store recent logs for debugging
  static storeRecentLog(logEntry) {
    if (!this.recentLogs) {
      this.recentLogs = [];
    }

    this.recentLogs.push(logEntry);

    // Keep only last 100 logs
    if (this.recentLogs.length > 100) {
      this.recentLogs.shift();
    }
  }

  // Get session information
  static getSessionInfo() {
    return {
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.getSessionId(),
    };
  }

  // Get or create session ID
  static getSessionId() {
    if (!this._sessionId) {
      this._sessionId =
        "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
    }
    return this._sessionId;
  }

  // Convenience methods for different log levels
  static debug(message, data = null, context = null) {
    this.log("debug", message, data, context);
  }

  static info(message, data = null, context = null) {
    this.log("info", message, data, context);
  }

  static warn(message, data = null, context = null) {
    this.log("warn", message, data, context);
  }

  static error(message, data = null, context = null) {
    this.log("error", message, data, context);
  }

  // Performance logging
  static performance(operation, duration, data = null) {
    this.log(
      "info",
      `Performance: ${operation} took ${duration}ms`,
      {
        operation,
        duration,
        ...data,
      },
      "Performance"
    );
  }

  // API call logging
  static api(method, url, status, duration, data = null) {
    const level = status >= 400 ? "error" : "info";
    this.log(
      level,
      `API ${method} ${url} - ${status} (${duration}ms)`,
      {
        method,
        url,
        status,
        duration,
        ...data,
      },
      "API"
    );
  }

  // User action logging
  static userAction(action, data = null) {
    this.log("info", `User Action: ${action}`, data, "UserAction");
  }

  // Security logging
  static security(event, data = null) {
    this.log("warn", `Security Event: ${event}`, data, "Security");
  }

  // System health logging
  static health(component, status, data = null) {
    const level = status === "healthy" ? "info" : "warn";
    this.log(level, `Health Check: ${component} is ${status}`, data, "Health");
  }

  // Get recent logs for debugging
  static getRecentLogs(count = 50) {
    if (!this.recentLogs) return [];
    return this.recentLogs.slice(-count);
  }

  // Clear recent logs
  static clearRecentLogs() {
    this.recentLogs = [];
  }

  // Export logs for debugging
  static exportLogs() {
    const logs = this.getRecentLogs();
    const blob = new Blob([JSON.stringify(logs, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `crescendo-logs-${new Date().toISOString().slice(0, 19)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Environment-specific logging configuration
  static configure(options = {}) {
    if (options.level) {
      this.setLevel(options.level);
    }

    if (options.enableSentry !== undefined) {
      this._sentryEnabled = options.enableSentry;
    }

    if (options.customEndpoint) {
      // Store custom endpoint in a static property instead of modifying process.env
      this._customLoggingEndpoint = options.customEndpoint;
    }
  }

  // Check if a specific log level is enabled
  static isLevelEnabled(level) {
    const levelPriority = this.LOG_LEVELS[level] || this.LOG_LEVELS.info;
    return levelPriority >= this.currentLevel;
  }

  // Get logging statistics
  static getStats() {
    if (!this.recentLogs) return {};

    const stats = {
      total: this.recentLogs.length,
      byLevel: {},
      byContext: {},
      timeRange: {
        oldest: null,
        newest: null,
      },
    };

    this.recentLogs.forEach((log) => {
      // Count by level
      stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1;

      // Count by context
      if (log.context) {
        stats.byContext[log.context] = (stats.byContext[log.context] || 0) + 1;
      }

      // Update time range
      if (!stats.timeRange.oldest || log.timestamp < stats.timeRange.oldest) {
        stats.timeRange.oldest = log.timestamp;
      }
      if (!stats.timeRange.newest || log.timestamp > stats.timeRange.newest) {
        stats.timeRange.newest = log.timestamp;
      }
    });

    return stats;
  }

  // Filter logs by criteria
  static filterLogs(criteria = {}) {
    if (!this.recentLogs) return [];

    return this.recentLogs.filter((log) => {
      if (criteria.level && log.level !== criteria.level) return false;
      if (criteria.context && log.context !== criteria.context) return false;
      if (criteria.message && !log.message.includes(criteria.message))
        return false;
      if (criteria.since && new Date(log.timestamp) < new Date(criteria.since))
        return false;
      if (criteria.until && new Date(log.timestamp) > new Date(criteria.until))
        return false;

      return true;
    });
  }

  // Sanitize sensitive data from logs
  static sanitizeData(data) {
    if (!data || typeof data !== "object") return data;

    const sanitized = { ...data };
    const sensitiveKeys = [
      "password",
      "token",
      "secret",
      "key",
      "authorization",
      "cookie",
    ];

    Object.keys(sanitized).forEach((key) => {
      const lowerKey = key.toLowerCase();
      for (const sensitiveKey of sensitiveKeys) {
        if (lowerKey.includes(sensitiveKey)) {
          sanitized[key] = "[REDACTED]";
          break;
        }
      }

      // Recursively sanitize nested objects
      if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      }
    });

    return sanitized;
  }

  // Advanced filtering for data objects
  static filterDataForLogging(data, sensitiveKeys = []) {
    if (!data) return data;

    const allSensitiveKeys = [
      ...sensitiveKeys,
      "password",
      "token",
      "secret",
      "key",
      "authorization",
      "cookie",
    ];

    return Object.keys(data).reduce((filtered, key) => {
      const lowerKey = key.toLowerCase();
      const isSensitive = allSensitiveKeys.some((sensitiveKey) =>
        lowerKey.includes(sensitiveKey.toLowerCase())
      );

      if (isSensitive) {
        filtered[key] = "[REDACTED]";
      } else if (typeof data[key] === "object" && data[key] !== null) {
        filtered[key] = this.filterDataForLogging(data[key], sensitiveKeys);
      } else {
        filtered[key] = data[key];
      }

      return filtered;
    }, {});
  }

  // Log level management
  static setLevel(level) {
    if (Object.prototype.hasOwnProperty.call(this.LOG_LEVELS, level)) {
      // Store level in a static property instead of trying to modify process.env
      this._logLevel = level;
      this.info(`Log level changed to: ${level}`);
    } else {
      this.warn(`Invalid log level: ${level}`);
    }
  }

  // Get current configuration
  static getConfig() {
    return {
      currentLevel: Object.keys(this.LOG_LEVELS).find(
        (key) => this.LOG_LEVELS[key] === this.currentLevel
      ),
      environment: EnvironmentConfig.environment,
      externalLogging:
        EnvironmentConfig.isProduction || EnvironmentConfig.isStaging,
      sentryEnabled: !!EnvironmentConfig.monitoring.sentryDsn,
      customEndpoint: !!(
        this._customLoggingEndpoint || process.env.VUE_APP_LOGGING_ENDPOINT
      ),
    };
  }
}

// Define static properties outside the class to avoid syntax issues
Logger.LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

Logger.COLORS = {
  debug: "#6B7280", // Gray
  info: "#3B82F6", // Blue
  warn: "#F59E0B", // Yellow/Orange
  error: "#EF4444", // Red
};

export default Logger;
